import React, { useCallback, useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import {
  addContactToProject,
  createProjectContactGroup,
  deleteProjectContactGroup,
  fetchShowcaseContactGroups,
  removeContactFromProject,
  updateProjectContactGroup
} from '../api';

import { Button, FormControl } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import Group from './group';
import { showSnackbar } from 'components/ui/snackbar/helper';

function ProjectShowcaseContacts({ projectId }) {
  const [show, setShow] = useState(false);
  const [groups, setGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState(null);

  const groupNames = groups.map(group => group.name);

  const handleDataResponse = (data) => {
    const groupData = data['showcase/contactGroups'];
    setShow(true);

    if (Array.isArray(groupData) && groupData.length > 0) {
      setGroups(groupData);
    } else {
      setGroups([]);
    }
  };

  useEffect(() => {
    fetchShowcaseContactGroups({ projectId })
      .then(handleDataResponse)
      .catch(() => { /* noop - when the customization is not enabled, /showcase_contacts returns 401 */ });
  }, []);

  if (!show) return null;

  const onShowcaseContactSelect = (contact, groupId) => {
    addContactToProject({ contactId: contact.id, groupId, projectId }).then((response) => {
      showSnackbar({ text: `Contact "${contact.firstName} ${contact.lastName}" added!`, variant: 'success' });
      handleDataResponse(response);
    });
  };

  const onRemoveContact = (contact) => {
    removeContactFromProject({ projectContactId: contact.id, projectId }).then((response) => {
      showSnackbar({ text: `Contact "${contact.name}" removed!`, variant: 'danger' });
      handleDataResponse(response);
    });
  };

  const createGroup = () => {
    const newName = newGroupName?.trim();
    if (!newName) {
      setNewGroupName(null);
    } else if (groupNames.includes(newName)) {
      showSnackbar({ text: `Group "${newName}" already exists`, variant: 'warning' });
    } else if (newName.length < 3) {
      showSnackbar({ text: 'Group name must be at least 3 characters long', variant: 'warning' });
    } else {
      createProjectContactGroup({ projectId, name: newName }).then((data) => {
        handleDataResponse(data);
        setNewGroupName(null);
      });
    }
  };

  const updateGroup = (id, payload) => {
    updateProjectContactGroup({ projectId, groupId: id, showcaseContactGroup: payload }).then((data) => {
      handleDataResponse(data);
      showSnackbar({ text: 'Group updated!', variant: 'success' });
    });
  };

  const deleteGroup = (id) => {
    deleteProjectContactGroup({ projectId, groupId: id }).then((data) => {
      handleDataResponse(data);
      showSnackbar({ text: 'Group deleted!', variant: 'danger' });
    });
  };

  const renderNewGroupCTA = () => {
    if (newGroupName == null) return (
      <Button variant="primary" onClick={() => setNewGroupName('')}>
        + Create Group
      </Button>
    );

    return (
      <div className="d-flex align-items-center justify-content-between gap-2">
        <FormControl
          placeholder="Group Name"
          value={newGroupName}
          onChange={e => setNewGroupName(e.target.value)}
          onKeyUp={e => e.key === 'Enter' && createGroup()}
        />
        <Button
          aria-label="Create group"
          size="sm"
          variant="link"
          onClick={createGroup}
        >
          <FontAwesomeIcon icon={faCheck} />
        </Button>
      </div>
    );
  };

  return (
    <div className="mt-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h6 className="fw-light">Project Showcase Contacts</h6>
        {renderNewGroupCTA()}
      </div>
      {groups.map(g => (
        <Group
          initialGroup={g}
          key={g.name}
          projectId={projectId}
          onDelete={() => deleteGroup(g.id)}
          onRemoveContact={onRemoveContact}
          onShowcaseContactSelect={onShowcaseContactSelect}
          onUpdateGroup={updateGroup}
        />
      ))}
    </div>
  );
}

ProjectShowcaseContacts.propTypes = {
  projectId: PropTypes.number.isRequired
};

const mapStateToProps = state => ({
  projectId: state.project.id
});

export default connect(mapStateToProps)(ProjectShowcaseContacts);
